import { Controller } from "@hotwired/stimulus";

// LOC-078: a Locals conversation thread. Enter sends the reply (Shift+Enter is
// left to the browser for a line break), and the thread container, never the
// window, is kept scrolled to the newest message: on load and after each reply
// the stream has appended.
//
// Jane's 2026-10-12 report: turbo:submit-end fires before the streams render
// (each stream renders one animation frame after its turbo:before-stream-render),
// so a scroll scheduled on submit-end ran against the old thread height, and the
// focus hit the textarea the last stream then replaced. The scroll now follows
// the thread's own DOM (a MutationObserver on the container), and the focus
// follows the new textarea when Stimulus connects it. A second Enter while a
// send is pending is ignored, so a slow stream cannot create a duplicate.
export default class extends Controller {
  static targets = ["thread", "input", "error"];

  connect() {
    this.pending = false;
    this.focusNextInput = false;
    this.scrollToBottom();
    if (this.hasThreadTarget) {
      this.observer = new MutationObserver(() => this.scrollToBottom());
      this.observer.observe(this.threadTarget, { childList: true });
    }
    // A booking request lands here (#locals-thread-end): the poster types next.
    if (window.location.hash === "#locals-thread-end" && this.hasInputTarget) this.inputTarget.focus();
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
  }

  keydown(event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing) return;

    event.preventDefault();
    if (this.pending) return;
    const form = event.target.form;
    if (form) form.requestSubmit();
  }

  // turbo:submit-start on the form.
  submitting() {
    this.pending = true;
  }

  // turbo:submit-end on the form. The streams render after this event, so the
  // scroll and the focus are left to the observer and the target callback.
  submitted(event) {
    this.pending = false;
    this.focusNextInput = Boolean(event.detail.success);
  }

  // The reply stream replaces the textarea with an empty one; focus it when it
  // arrives, so the member can type the next line at once.
  inputTargetConnected(input) {
    if (!this.focusNextInput) return;

    this.focusNextInput = false;
    this.scrollToBottom();
    input.focus();
  }

  scrollToBottom() {
    if (!this.hasThreadTarget) return;
    this.threadTarget.scrollTop = this.threadTarget.scrollHeight;
  }
}
